import { forwardRef, useId, type ComponentProps, type CSSProperties, type ReactNode } from 'react';
import type { TextInputProps } from '@mantine/core';
import { useTheme } from '@contexts/ThemeContext';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  getJarvisControlStyle,
  jarvisControlClassName,
  renderFieldDescription,
  renderFieldError,
  renderFieldLabel,
  type JarvisFieldSize,
} from './jarvis-field-utils';

export type JarvisTextInputProps = TextInputProps;

/** Mantine layout / style-system keys that must not reach the DOM. */
const MANTINE_LAYOUT_KEYS = new Set([
  'm',
  'mx',
  'my',
  'mt',
  'mb',
  'ml',
  'mr',
  'p',
  'px',
  'py',
  'pt',
  'pb',
  'pl',
  'pr',
  'w',
  'miw',
  'maw',
  'h',
  'mih',
  'mah',
  'flex',
  'display',
  'bg',
  'c',
  'ff',
  'fz',
  'fw',
  'lts',
  'ta',
  'lh',
  'tt',
  'td',
  'hiddenFrom',
  'visibleFrom',
  'lightHidden',
  'darkHidden',
]);

/**
 * JarvisTextInput - Themed text field wrapping shadcn Input.
 * Keeps the previous Mantine TextInput-compatible props API (label, error,
 * description, size, styles.input, left/rightSection, etc.) so call sites
 * do not need to change.
 */
export const JarvisTextInput = forwardRef<HTMLInputElement, JarvisTextInputProps>((props, ref) => {
  const { colors: themeColors } = useTheme();
  const autoId = useId();

  const {
    label,
    description,
    error,
    size = 'sm',
    styles,
    className,
    style,
    required,
    withAsterisk,
    leftSection,
    rightSection,
    disabled,
    id,
    name,
    // Mantine-only / non-DOM props — strip so they never hit <input>
    radius: _radius,
    variant: _variant,
    classNames: _classNames,
    unstyled: _unstyled,
    wrapperProps: _wrapperProps,
    inputWrapperOrder: _inputWrapperOrder,
    inputContainer: _inputContainer,
    labelProps: _labelProps,
    descriptionProps: _descriptionProps,
    errorProps: _errorProps,
    leftSectionWidth: _leftSectionWidth,
    rightSectionWidth: _rightSectionWidth,
    leftSectionPointerEvents: _leftSectionPointerEvents,
    rightSectionPointerEvents: _rightSectionPointerEvents,
    withErrorStyles: _withErrorStyles,
    pointer: _pointer,
    __staticSelector: _staticSelector,
    mod: _mod,
    vars: _vars,
    attributes: _attributes,
    ...rest
  } = props as TextInputProps & Record<string, unknown>;

  const inputProps: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rest)) {
    if (MANTINE_LAYOUT_KEYS.has(key) || key.startsWith('__')) continue;
    inputProps[key] = value;
  }

  const fieldId = (id as string | undefined) ?? autoId;
  const hasError = Boolean(error);
  const hasLeft = leftSection != null && leftSection !== false;
  const hasRight = rightSection != null && rightSection !== false;

  // `style` on Mantine TextInput applies to the root wrapper; `styles.input` to the control.
  const controlStyle = getJarvisControlStyle(themeColors, {
    hasError,
    styles,
    style: undefined,
  });

  const control = (
    <Input
      {...(inputProps as ComponentProps<typeof Input>)}
      ref={ref}
      id={fieldId}
      name={name as string | undefined}
      disabled={disabled as boolean | undefined}
      required={required as boolean | undefined}
      aria-invalid={hasError || undefined}
      className={cn(
        jarvisControlClassName(size as JarvisFieldSize, className as string | undefined),
        hasLeft && 'pl-9',
        hasRight && 'pr-9',
        hasError && 'border-destructive focus-visible:ring-destructive'
      )}
      style={controlStyle}
    />
  );

  return (
    <div className="w-full" style={style as CSSProperties | undefined}>
      {renderFieldLabel({
        id: fieldId,
        label: label as ReactNode,
        required: required as boolean | undefined,
        withAsterisk: withAsterisk as boolean | undefined,
        textColor: themeColors.text,
      })}

      {hasLeft || hasRight ? (
        <div className="relative w-full">
          {hasLeft ? (
            <span className="pointer-events-none absolute inset-y-0 left-0 z-10 flex items-center pl-2.5 text-muted-foreground">
              {leftSection as ReactNode}
            </span>
          ) : null}
          {control}
          {hasRight ? (
            <span className="absolute inset-y-0 right-0 z-10 flex items-center pr-2.5 text-muted-foreground">
              {rightSection as ReactNode}
            </span>
          ) : null}
        </div>
      ) : (
        control
      )}

      {renderFieldDescription(description as ReactNode, themeColors.textSecondary)}
      {renderFieldError(error as ReactNode, themeColors.error)}
    </div>
  );
});

JarvisTextInput.displayName = 'JarvisTextInput';

export default JarvisTextInput;
